/**
 * The whole of the release page while the system is not up.
 *
 * The release flow used to be a tour of two VMs: the operator learned which
 * machine each phase ran on, spotted which one was off, and pressed Start on the
 * right card at the right moment. The machines are an implementation detail of a
 * tool whose job is to guide someone through a release, so the flow now has one
 * subject — the release system — and this panel is the one state it can be in that
 * has nothing to do with the release: it is asleep, and the one thing to do is wake
 * it.
 *
 * It replaces the page rather than sitting above it, for the same reason the
 * legality table refuses every action here: nothing on the page can run, and a
 * disabled flow under a wake button is a page that invites clicks it will reject
 * (v1.1266). What it keeps is the promise that nothing was lost — the operator of a
 * release that has been running for an hour is entitled to know that the hour is
 * still there, in git and on the system's disk.
 *
 * The per-machine view is still one click away, and this is the only place in the
 * flow that says so: troubleshooting genuinely is about a particular machine, and
 * the host pages are where that work happens.
 */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    AlertTitle,
    Button,
    CircularProgress,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import BedtimeOutlined from '@mui/icons-material/BedtimeOutlined';
import {
    hostInstance,
    unprovisionedHostMessage,
    type HostRole,
} from '@finvari/deployer';
import { startHostOn, startHostTryingAll, wakeHosts, type WakeFailure } from '../lib/hostPower';
import { StartFailureAlert, type MachineTypeRequest } from './MachineTypeMenu';
import { hostStatusLine, hostView, type HostEscalation, type HostStatusLine } from '../lib/hostStatus';
import type { SystemView } from '../lib/releaseSystem';
import { LIVE_TICK_MS, useHost, useNow } from '../hooks';

/** How each machine reads when the panel has to name one. */
const ROLE_LABEL: Record<HostRole, string> = {
    prepare: 'prepare host',
    release: 'release host',
};

/**
 * This panel has no SSH command on it — the flow does not talk about machines — so
 * a boot that has overrun is escalated to the page that does.
 */
const PANEL_ESCALATION: HostEscalation = {
    remediation: 'open the host page below for its health checks and the SSH command',
};

export interface SystemSleepingPanelProps {
    system: SystemView;
    /** The page heading the flow would have shown: "Release control", "Preparing v1.2" … */
    heading: string;
    /**
     * What is waiting on the wake, in the operator's terms ("v1.1266 is in review").
     * The panel states it rather than the phase, because the phase is what the flow
     * behind this panel would have explained and the operator cannot see it.
     */
    waitingOn?: string;
}

export function SystemSleepingPanel({ system, heading, waitingOn }: SystemSleepingPanelProps) {
    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h1">
                    {heading}
                </Typography>
                {waitingOn && (
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
                        {waitingOn}
                    </Typography>
                )}
            </Stack>

            {system.state === 'sleeping' ? <GoingToSleepAlert /> : <WakeAlert system={system} />}

            <TroubleshootingLine />
        </Stack>
    );
}

/**
 * A sleep the operator asked for, mid-flight. Waking now would race a shutdown
 * already under way — Compute either refuses it or brings back a VM that is still
 * powering off — so the panel waits it out and says how long that is.
 */
function GoingToSleepAlert() {
    return (
        <Alert severity="info" icon={<BedtimeOutlined />}>
            <AlertTitle>The release system is going to sleep</AlertTitle>
            It is shutting down now. Waking it before that finishes would race the shutdown, so this page offers the
            wake again once it is down — about a minute.
        </Alert>
    );
}

/** The panel's one action, and everything the operator needs before pressing it. */
function WakeAlert({ system }: { system: SystemView }) {
    const [sending, setSending] = useState(false);
    const [errors, setErrors] = useState<WakeFailure[]>([]);

    const startable = system.wakeTargets.filter((role) => hostInstance(role));
    const unregistered = system.wakeTargets.filter((role) => !hostInstance(role));
    const waking = system.state === 'waking';

    /**
     * Start every VM that is not already up, not only the one this phase needs. That
     * is the trade the whole change makes: a release uses both machines, and an
     * operator who has to work out which one is due is back to operating the VMs
     * instead of running a release. Both stop themselves again when idle, so the
     * cost of the spare one is a couple of idle hours.
     */
    const wake = async () => {
        setSending(true);
        setErrors([]);
        setErrors(await wakeHosts(startable));
        setSending(false);
    };

    /**
     * Retry one machine on a chosen shape after a stockout, leaving the other
     * host's outcome alone: a wake starts both, and only one of them is ever the
     * one that had no capacity.
     */
    const retryOn = async (role: HostRole, request: MachineTypeRequest) => {
        setSending(true);
        setErrors((prev) => prev.filter((f) => f.role !== role));
        const failure = request.kind === 'all' ? await startHostTryingAll(role) : await startHostOn(role, request.machineType);
        if (failure) setErrors((prev) => [...prev, { role, ...failure }]);
        setSending(false);
    };

    const button = (
        <Button
            variant="contained"
            onClick={() => void wake()}
            disabled={sending || startable.length === 0}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
            {sending ? 'Waking…' : waking ? 'Wake again' : 'Wake the release system'}
        </Button>
    );

    return (
        <Stack spacing={2}>
            <Alert severity={system.overdue ? 'warning' : 'info'} icon={<BedtimeOutlined />}>
                <AlertTitle>
                    {waking ? 'Waking the release system' : 'The release system is asleep'}
                </AlertTitle>
                <Stack spacing={1}>
                    <Typography variant="body2">
                        {waking
                            ? 'The machines are booting. This page shows the release as soon as the system is answering.'
                            : // Said every time, because the operator looking at this has a
                              // release in progress and no way to tell from here whether an
                              // hour of preparation survived the stop. It did: the milestones
                              // are in git and the caches and markers are on the system's own
                              // disk, which is why it is allowed to sleep at all.
                              'It shuts itself down when nothing has run for a while, which costs nothing: every milestone is in git and everything else is on its own disk, so waking it resumes where it left off. It takes about two minutes.'}
                    </Typography>
                    <BootProgress system={system} />
                </Stack>
            </Alert>

            {/*
              * An unregistered VM is unfinished configuration rather than a machine
              * that will not start, and the fix is a command, so it is said here in
              * full instead of arriving as a failed wake.
              */}
            {unregistered.map((role) => (
                <Alert key={role} severity="warning">
                    <AlertTitle>The {ROLE_LABEL[role]} VM is not registered</AlertTitle>
                    {unprovisionedHostMessage(role)}
                </Alert>
            ))}

            {errors.map((failure) => (
                <StartFailureAlert
                    key={failure.role}
                    role={failure.role}
                    failure={failure}
                    title={`The ${ROLE_LABEL[failure.role]} did not start`}
                    onChoose={(request) => void retryOn(failure.role, request)}
                    sending={sending}
                />
            ))}

            {startable.length > 0 && button}
        </Stack>
    );
}

/**
 * How the boot is going, per machine. The only place the panel names them, and it
 * has to: a wake starts two VMs and one of them can be the one that is late, which
 * is the difference between waiting and going to look at the host page.
 */
function BootProgress({ system }: { system: SystemView }) {
    const prepare = useHost('prepare');
    const release = useHost('release');
    // The elapsed seconds are the content here, so this view keeps the second hand
    // whatever the rest of the page is doing.
    const now = useNow(LIVE_TICK_MS);

    // Only while something is happening: two "Offline — last heartbeat 3h ago" lines
    // under "the system is asleep" say nothing the alert has not, and an operator
    // reads a list of machines as a list of things they have to deal with.
    if (system.state !== 'waking') return null;

    const lines: Array<{ role: HostRole; line: HostStatusLine }> = [];
    for (const [role, host] of [['prepare', prepare], ['release', release]] as const) {
        const line = hostStatusLine(hostView(host.value, now, host.loading), hostInstance(role), PANEL_ESCALATION);
        if (line) lines.push({ role, line });
    }

    return (
        <Stack spacing={0.25}>
            {lines.map(({ role, line }) => (
                <Typography key={role} variant="body2" color={line.color}>
                    {ROLE_LABEL[role]}: {line.text}
                </Typography>
            ))}
        </Stack>
    );
}

/**
 * The flow's one acknowledgement that the system is two machines. It is a caption
 * rather than a pair of cards on purpose: the per-machine controls, health checks
 * and instruction history are what the host pages are for, and offering them here
 * is what made the flow a tour of the VMs.
 */
function TroubleshootingLine() {
    return (
        <Typography variant="caption" color="text.secondary">
            Not coming up? The machines are the{' '}
            <Link component={RouterLink} to="/hosts/prepare">
                prepare host
            </Link>{' '}
            and the{' '}
            <Link component={RouterLink} to="/hosts/release">
                release host
            </Link>
            , where their health checks, logs and SSH commands live.
        </Typography>
    );
}
